import cx from 'classnames';
import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageLoader from '../../../components/other/PageLoader';
import { WEEKDAYS } from '../../../constants';
import { STATIC_ROUTES } from '../../../constants/routes';
import { useAuth } from '../../../container/authContext';
import { usePermission } from '../../../container/permissionContext';
import useOutsideClick from '../../../hook/useOutsideClick';
import Layout from '../../../layout/default';
import { deleteCNATaskCommentService, getCNAMobileShiftsData } from '../../../services/cna/assignments/cna';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { isResponseOk } from '../../../utility/isResponseOk';
import TaskCommentsModal from '../assignments/unit/components/add-task-comment-modal';
import ChangeCNAModal from '../assignments/unit/components/change-cna-modal';
import EditCommentModal from '../assignments/unit/components/edit-comment-modal';
import { cnaColors } from '../assignments/unit/constants';
import './styles.scss';
import { extractBedParts } from '../../../utility';
import { TaskMenu } from '../assignments/unit/components/task-menu';
import dayjs from 'dayjs';

const StaffEditBtn = ({ onClick }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ cursor: 'pointer', flexShrink: 0 }}
    onClick={onClick}
  >
    <g clipPath="url(#clip0_10626_59049)">
      <path
        d="M9.16675 3.33027H3.33341C2.89139 3.33027 2.46746 3.50586 2.1549 3.81842C1.84234 4.13098 1.66675 4.55491 1.66675 4.99693V16.6636C1.66675 17.1056 1.84234 17.5296 2.1549 17.8421C2.46746 18.1547 2.89139 18.3303 3.33341 18.3303H15.0001C15.4421 18.3303 15.866 18.1547 16.1786 17.8421C16.4912 17.5296 16.6667 17.1056 16.6667 16.6636V10.8303M15.4167 2.08027C15.7483 1.74875 16.1979 1.5625 16.6667 1.5625C17.1356 1.5625 17.5852 1.74875 17.9167 2.08027C18.2483 2.41179 18.4345 2.86143 18.4345 3.33027C18.4345 3.79911 18.2483 4.24875 17.9167 4.58027L10.0001 12.4969L6.66675 13.3303L7.50008 9.99693L15.4167 2.08027Z"
        stroke="#7F56D9"
        strokeWidth="1.67"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_10626_59049">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const CNAMobileAssignmentsPage = () => {
  let previousRowSelected = null;
  const buildDateFromParts = (parts) => {
    // parts.month is "04", parts.day is "27", etc.
    return new Date(`${parts.year}-${parts.month}-${parts.day}T12:00:00`);
    // 👆 midday avoids timezone edge issues
  };

  const shifts = ['7-3', '3-11', '11-7'];
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  });

  const getFormattedParts = (date) => {
    const parts = formatter.formatToParts(date);
    return Object.fromEntries(parts.map((p) => [p.type, p.value]));
  };

  const currentDateAndShiftData = useMemo(() => {
    const now = new Date();

    const map = getFormattedParts(now);

    const hour = Number(map.hour);

    let shift;
    if (hour >= 7 && hour < 15) {
      shift = '7-3';
    } else if (hour >= 15 && hour < 23) {
      shift = '3-11';
    } else {
      shift = '11-7';
    }

    let dateToFormat = now;

    // 👇 adjust date for night shift
    if (shift === '11-7') {
      dateToFormat = new Date(now);
      dateToFormat.setDate(dateToFormat.getDate() - 1);
    }

    const adjustedParts = formatter.formatToParts(dateToFormat);
    const adjustedMap = Object.fromEntries(adjustedParts.map((p) => [p.type, p.value]));

    return {
      dateParts: adjustedMap,
      shift,
    };
  }, []);

  const navigate = useNavigate();

  const location = useLocation();

  const { user = {} } = useAuth();
  const { assignmentID } = useParams();
  const tableRef = useRef(null);
  const staffCNAListRef = useRef(null);
  const [shiftCounter, setShiftCounter] = useState(0);
  const { state: locationState } = useLocation();

  const [currentShift, setCurrentShift] = useState(currentDateAndShiftData.shift);
  const [currentDate, setCurrentDate] = useState(currentDateAndShiftData.dateParts);
  const [shiftsData, setShiftsData] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [taskCommentsModalData, setTaskCommentsModalData] = useState(null);
  const [editCommentModalData, setEditCommentModalData] = useState(null);

  const handleGetShiftsData = useCallback(async () => {
    try {
      const dateStr = currentDate.year + '-' + currentDate.month + '-' + currentDate.day;
      const params = { date: dateStr, shift: currentShift.replace('-', '_') };

      const response = await getCNAMobileShiftsData(params);
      if (isResponseOk(response) && response.data) {
        setShiftsData(response.data);
      }
    } catch (e) {
      console.error(e);
    }
  }, [JSON.stringify(currentDate), currentShift]);

  useEffect(() => {
    handleGetShiftsData();
  }, [currentDate, currentShift]);

  const handleChangeShift = (shiftIncrement) => {
    const currentIndex = shifts.indexOf(currentShift);
    let newIndex = currentIndex + shiftIncrement;

    let newDate = buildDateFromParts(currentDate);

    // 👉 handle overflow forward
    if (newIndex >= shifts.length) {
      newIndex = 0;
      newDate.setDate(newDate.getDate() + 1);
    }

    // 👉 handle overflow backward
    if (newIndex < 0) {
      newIndex = shifts.length - 1;
      newDate.setDate(newDate.getDate() - 1);
    }

    // 👉 format again in NY timezone
    const newParts = getFormattedParts(newDate);

    setCurrentShift(shifts[newIndex]);
    setCurrentDate(newParts);
  };

  const handleSetTaskCommentsData = (roomID, { id, name }) => {
    const data = { id, name, roomID };
    setTaskCommentsModalData(data);
  };

  const handleDeleteComment = async (taskID, commentID) => {
    try {
      setIsLoading(true);
      const res = await deleteCNATaskCommentService({ taskID, commentID });
      if (isResponseOk(res)) {
        await handleGetShiftsData();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const renderMobileHeaderShifts = () => {
    return (
      <div className={'header__shifts'}>
        <button className={`btn btn-outline pagination-btn`} onClick={() => handleChangeShift(-1)}>
          <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5.83496 0.835938L0.834961 5.83594L5.83496 10.8359"
              stroke="#101828"
              strokeWidth="1.67"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className={cx('header__shifts__item')}>
          <p className={'date'}>
            {currentDate.weekday}, {currentDate.month}/{currentDate.day}/{currentDate.year}
          </p>
          <p className={'name'}>SHIFT {currentShift}</p>
          <br />
        </div>
        <button className={`btn btn-outline pagination-btn `} onClick={() => handleChangeShift(1)}>
          <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0.834961 10.8359L5.83496 5.83594L0.834961 0.835938"
              stroke="#101828"
              strokeWidth="1.67"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    );
  };

  const renderMobileHeader = () => {
    return (
      <header>
        <div className="header__side left"></div>
        <div className="header__side right">{renderMobileHeaderShifts()}</div>
      </header>
    );
  };

  const renderMobileRecords = (shiftRecords) => {
    return (
      <div className="cna-assignments-records">
        {shiftRecords.map((record, index) => (
          <div key={index} className="record-item" style={{ backgroundColor: cnaColors[index % cnaColors.length].bg }}>
            <div className="record-item-header">
              <div className="record-item-header-right">
                <p className="room-label">Room {record.unit_bed.bed}</p>
              </div>
              <div className="record-item-header-left">
                <p className="cna-name">
                  {record.unit_bed.resident?.first_name} {record.unit_bed.resident?.last_name}
                </p>
              </div>
            </div>

            <div className="record-item-tasks">
              {!!record.tasks.length &&
                record.tasks.map((task, taskIndex) => (
                  <div key={taskIndex} className="record-item-task">
                    <div className="record-item-task-header">
                      <div className="record-item-task-header-left">
                        <span className="task-index">{taskIndex + 1}.</span> <span className="task-name">{task.name}</span>
                      </div>
                      <div className="record-item-task-header-right">
                        <TaskMenu
                          canManageTasks
                          task={task}
                          shiftRecordID={record.id}
                          hasResident={!!record.unit_bed.resident}
                          handleSetTaskCommentsData={handleSetTaskCommentsData}
                        />
                      </div>
                    </div>

                    {!!task.comments?.length && (
                      <div className="task-comments-block" style={{ backgroundColor: cnaColors[index % cnaColors.length].inner }}>
                        <div className="task-comments-list">
                          {task.comments.map((comment, commentIndex) => (
                            <div key={comment.id} className="task-comment-item">
                              <div className="comment-index">{commentIndex + 1}.</div>
                              <div className="comment-content">{comment.text}</div>
                              <div className="comment-actions">
                                <i
                                  className="icon-icon-edit"
                                  style={{ cursor: 'pointer', fontSize: 16 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditCommentModalData({ ...comment, taskID: task.id });
                                  }}
                                />
                                <i
                                  className="icon-remove"
                                  style={{ cursor: 'pointer', color: '#B32318', fontSize: 16 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteComment(task.id, comment.id);
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderUnits = () => {
    return (
      <div className="units-list">
        {shiftsData
          .filter((unit) => !!unit.shift_records.length)
          .map((unit) => (
            <div className="units-list-item">
              <div className="unit-name">{unit.name}</div>
              {renderMobileRecords(unit.shift_records)}
            </div>
          ))}
      </div>
    );
  };

  return (
    <Layout title={'CNA Unit'} className="unit-page-mobile-layout">
      <div className="page-wrapper">
        {renderMobileHeader()}
        {renderUnits()}
        {taskCommentsModalData && (
          <TaskCommentsModal
            data={taskCommentsModalData}
            onClose={() => setTaskCommentsModalData(null)}
            onRefetchAssignment={handleGetShiftsData}
          />
        )}
        {editCommentModalData && (
          <EditCommentModal
            commentData={editCommentModalData}
            onRefetchAssignment={handleGetShiftsData}
            onClose={() => setEditCommentModalData(null)}
          />
        )}
        {isLoading && <PageLoader />}
      </div>
    </Layout>
  );
};

export default CNAMobileAssignmentsPage;
